import { Edit2, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { CardTitle } from '@/components/ui/card'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/utils/cn'

export interface DetailCardHeadingProps {
  /** A lucide icon element carrying only its colour; the heading sizes it. */
  icon: ReactNode
  title: string
  titleClassName?: string
  /** Omit to hide edit/delete, e.g. when viewing a read-only (non-latest) version. */
  actions?: { onEdit: () => void; onDelete: () => void }
}

/**
 * Icon, title and edit/delete row shared by the schema detail cards. A long type
 * name is cut with an ellipsis (full name on hover) so it never runs under the
 * buttons.
 */
export function DetailCardHeading({
  icon,
  title,
  titleClassName,
  actions,
}: DetailCardHeadingProps) {
  return (
    // min-w-0: CardHeader is a grid, so without it the row would size to the
    // full title and push the actions out of the card.
    // Below md a long title wraps instead of being cut, and the actions drop
    // to a second line under it.
    <div className="flex max-md:flex-wrap min-w-0 items-center gap-2 [&>svg]:size-5 [&>svg]:shrink-0">
      {icon}
      <CardTitle
        className={cn(
          'flex-1 max-md:basis-auto truncate max-md:whitespace-normal max-md:break-words text-foreground',
          titleClassName
        )}
        title={title}
      >
        {title}
      </CardTitle>
      {actions && <DetailCardActions actions={actions} />}
    </div>
  )
}

/**
 * Edit / delete pair. Delete has no confirmation dialog, and on the phone it is
 * a thumb-sized button right beside Edit, so there one tap only arms it: the
 * pair turns into Delete + Cancel and the second tap deletes. Cancel takes the
 * place Delete had, so a double tap cancels instead of deleting. Desktop keeps
 * the single click. The cards key the heading by the item's id, so selecting
 * another item disarms.
 */
function DetailCardActions({
  actions,
}: {
  actions: NonNullable<DetailCardHeadingProps['actions']>
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [armed, setArmed] = useState(false)
  if (isMobile && armed) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="destructive"
          size="xs"
          onClick={() => {
            setArmed(false)
            actions.onDelete()
          }}
          className="h-9 border-destructive"
        >
          <Trash2 />
          {t('common.delete')}
        </Button>
        <Button variant="outline" size="xs" onClick={() => setArmed(false)} className="h-9">
          {t('common.cancel')}
        </Button>
      </div>
    )
  }
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button variant="outline" size="xs" onClick={actions.onEdit} className="max-md:h-9">
        <Edit2 />
        {t('common.edit')}
      </Button>
      <Button
        variant="outline"
        size="xs"
        onClick={isMobile ? () => setArmed(true) : actions.onDelete}
        className="max-md:h-9 border-destructive/30 text-destructive hover:bg-destructive/10"
      >
        <Trash2 />
        {t('common.delete')}
      </Button>
    </div>
  )
}
